import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Terminal, ShieldCheck, Cpu, ArrowRight } from "lucide-react";

function formatErr(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

const bootLines = [
  "> initializing AURA-QUANT kernel v4.2 ...",
  "> loading macro tensor [26 series] ... OK",
  "> calibrating regime classifier ... OK",
  "> mounting scoring engine [8 factors] ... OK",
  "> point-in-time guard: ENABLED",
  "> awaiting security clearance ...",
];

export default function Login() {
  const { login, register, user } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [boot, setBoot] = useState(0);

  useEffect(() => { if (user) nav("/"); }, [user, nav]);
  useEffect(() => {
    const t = setInterval(() => setBoot((b) => (b < bootLines.length ? b + 1 : b)), 350);
    return () => clearInterval(t);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(email, password, name || "Analyst");
    } catch (e2) {
      setErr(formatErr(e2.response?.data?.detail) || e2.message);
    } finally { setLoading(false); }
  };

  const demo = async () => {
    setErr(""); setLoading(true);
    try { await login("quant@aura.io", "quant123"); }
    catch (e2) { setErr(formatErr(e2.response?.data?.detail) || e2.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-stretch">
      {/* left telemetry */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 p-12 relative overflow-hidden border-r border-[#1E293B]"
        style={{ background: "linear-gradient(135deg,#0A0E16 0%,#0E131F 100%)" }}>
        <div className="absolute inset-0 opacity-20"
          style={{ backgroundImage: "url(https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400)", backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="relative">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-md flex items-center justify-center glow-cyan" style={{ background: "#00E5FF18" }}>
              <Cpu size={22} className="text-[#00E5FF]" />
            </div>
            <div>
              <div className="font-display text-2xl font-extrabold tracking-tight">AURA QUANT</div>
              <div className="eyebrow">// terminal v4.2</div>
            </div>
          </div>
        </div>
        <div className="relative font-mono text-[13px] text-[#5EEAD4] space-y-1.5">
          {bootLines.slice(0, boot).map((l, i) => (
            <div key={i} className="fade-up">{l}</div>
          ))}
          {boot >= bootLines.length && <div className="text-[#00E5FF] live-dot">> _</div>}
        </div>
        <div className="relative eyebrow">Measure the statistical edge of the present against history.</div>
      </div>

      {/* right form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <Cpu size={22} className="text-[#00E5FF]" />
            <span className="font-display text-2xl font-extrabold">AURA QUANT</span>
          </div>
          <div className="flex items-center gap-2 mb-1 text-[#00E5FF]">
            <Terminal size={16} />
            <span className="eyebrow" style={{ color: "#00E5FF" }}>secure access</span>
          </div>
          <h1 className="font-display text-3xl font-extrabold mb-6">
            {mode === "login" ? "Terminal Login" : "Request Clearance"}
          </h1>

          <form onSubmit={submit} className="space-y-3">
            {mode === "register" && (
              <input data-testid="auth-name-input" value={name} onChange={(e) => setName(e.target.value)}
                placeholder="Analyst name" className="w-full panel-2 rounded-md px-3 py-2.5 text-sm outline-none focus:border-[#00E5FF] transition-colors" />
            )}
            <input data-testid="auth-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="Email" className="w-full panel-2 rounded-md px-3 py-2.5 text-sm outline-none focus:border-[#00E5FF] transition-colors" />
            <input data-testid="auth-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="Password" className="w-full panel-2 rounded-md px-3 py-2.5 text-sm outline-none focus:border-[#00E5FF] transition-colors" />

            {err && <div data-testid="auth-error" className="text-xs font-mono text-[#EF4444] bg-[#EF444412] border border-[#EF444433] rounded px-3 py-2">{err}</div>}

            <button data-testid="auth-submit-button" disabled={loading} type="submit"
              className="w-full flex items-center justify-center gap-2 rounded-md py-2.5 text-sm font-semibold text-[#04121A] transition-transform hover:scale-[1.01] disabled:opacity-60"
              style={{ background: "#00E5FF" }}>
              {loading ? "Authenticating..." : mode === "login" ? "Enter Terminal" : "Create Account"}
              <ArrowRight size={16} />
            </button>
          </form>

          <button data-testid="auth-demo-button" onClick={demo} disabled={loading}
            className="mt-3 w-full flex items-center justify-center gap-2 rounded-md py-2.5 text-sm font-semibold border border-[#334155] text-slate-200 hover:bg-[#141C2E] transition-colors">
            <ShieldCheck size={16} className="text-[#00E5FF]" /> One-Click Demo Quant Access
          </button>

          <div className="mt-6 text-center text-xs font-mono text-slate-500">
            {mode === "login" ? "No clearance?" : "Have access?"}{" "}
            <button data-testid="auth-toggle-mode" onClick={() => { setMode(mode === "login" ? "register" : "login"); setErr(""); }}
              className="text-[#00E5FF] hover:underline">
              {mode === "login" ? "Request account" : "Login"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
